import {
  IonItem,
  IonLabel,
  IonNote,
  IonSelect,
  IonSelectOption,
  IonSpinner,
} from '@ionic/react';
import { useMemo } from 'react';
import { CompatibleMachine } from '../../../api/hooks/copy-scratchpad.hooks';
import i18n from '../../../i18n';

interface CompatibleMachinesSelectProps {
  loadingMachines: boolean;
  errorMachines: string | null;
  compatibleMachines: CompatibleMachine[] | undefined;
  currentMachineId: string;
  selectedMachineId?: string;
  onMachineSelect: (machineId: string) => void;
  copyLoading: boolean;
  disabled?: boolean;
}

export const CompatibleMachinesSelect = ({
  loadingMachines,
  errorMachines,
  compatibleMachines,
  currentMachineId,
  selectedMachineId,
  onMachineSelect,
  copyLoading,
  disabled,
}: CompatibleMachinesSelectProps) => {
  const stateContent = useMemo(() => {
    if (loadingMachines) {
      return (
        <>
          <IonNote style={{ marginRight: '8px' }}>
            {i18n.t('common:scratchpad_copy_loading_machines')}
          </IonNote>
          <IonSpinner name="dots" />
        </>
      );
    }

    if (errorMachines) {
      return (
        <IonNote color="danger">
          {i18n.t('common:scratchpad_copy_error_loading_machines')}
        </IonNote>
      );
    }

    return (
      <IonNote color="danger">
        {i18n.t('common:scratchpad_copy_no_compatible_machines')}
      </IonNote>
    );
  }, [loadingMachines, errorMachines]);

  if (compatibleMachines && compatibleMachines.length > 0) {
    return (
      <IonItem lines="full">
        <IonSelect
          value={selectedMachineId}
          onIonChange={(e) => onMachineSelect(e.detail.value)}
          placeholder={i18n.t('common:scratchpad_copy_select_machine')}
          disabled={copyLoading || disabled}
          label={i18n.t('common:scratchpad_copy_machine_label')}
        >
          {compatibleMachines.map((machine: CompatibleMachine) => (
            <IonSelectOption key={machine.id} value={machine.id}>
              {getMachineName(machine, currentMachineId)}
            </IonSelectOption>
          ))}
        </IonSelect>
      </IonItem>
    );
  }

  return (
    <IonItem lines="full">
      <IonLabel>{i18n.t('common:scratchpad_copy_machine_label')}</IonLabel>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {stateContent}
      </div>
    </IonItem>
  );
};

const getMachineName = (
  machine: CompatibleMachine,
  currentMachineId: string
) => {
  return `${machine.name}${
    currentMachineId === machine.id ? ` (${i18n.t('common:current')})` : ''
  }`;
};
